import type { MetadataApiProps } from "../types/report-template";

export type TemplateBlock = {
	id?: string;
	title?: string;
	section?: string;
	render?: string;
	source?: {
		type?: string;
		primary?: { api_id?: string; response_block?: string };
	};
	sources?: { api_id?: string }[];
	fields?: unknown;
	[key: string]: unknown;
};

export type TemplateSummaryItem = {
	id?: string;
	label?: string;
	block_id?: string;
	[key: string]: unknown;
};

export type TemplateConfig = {
	blocks?: TemplateBlock[];
	summary?: TemplateSummaryItem[];
	[key: string]: unknown;
};

export type ParsedTemplateConfig =
	| { config: TemplateConfig; error: null }
	| { config: null; error: string };

export type BlockSummary = {
	key: string;
	id: string;
	title: string;
	section: string;
	fieldCount: number;
	apiName: string;
	simple: boolean;
	active: boolean;
};

export function parseTemplateConfig(text: string): ParsedTemplateConfig {
	try {
		const parsed = JSON.parse(text);
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
			return { config: null, error: "O config precisa ser um objeto JSON." };
		}
		return { config: parsed, error: null };
	} catch (e) {
		return { config: null, error: e instanceof Error ? e.message : String(e) };
	}
}

export function isSimpleBlock(block: TemplateBlock): boolean {
	return (
		block.render === "key_value_grid" &&
		!block.sources &&
		block.source?.type === "api" &&
		!!block.source?.primary?.api_id &&
		Array.isArray(block.fields)
	);
}

export function emptyBlockTitles(config: TemplateConfig): string[] {
	return (config.blocks ?? [])
		.filter((block) => isSimpleBlock(block) && blockFields(block).length === 0)
		.map((block) => block.title || block.id || "");
}

function blockApiName(
	block: TemplateBlock,
	metadata: MetadataApiProps[],
): string {
	if (Array.isArray(block.sources) && block.sources.length > 1) {
		return "várias APIs";
	}
	const apiId =
		block.source?.primary?.api_id ?? block.sources?.[0]?.api_id ?? "";
	return metadata.find((api) => api.id === apiId)?.name ?? apiId;
}

export function summarizeBlocks(
	config: TemplateConfig,
	metadata: MetadataApiProps[],
): BlockSummary[] {
	if (!Array.isArray(config.blocks)) return [];
	const seen: Record<string, number> = {};
	return config.blocks.map((block: TemplateBlock) => {
		const id = block.id ?? "";
		seen[id] = (seen[id] ?? 0) + 1;
		return {
			key: `${id}#${seen[id]}`,
			id,
			title: block.title ?? "",
			section: block.section ?? "",
			fieldCount: Array.isArray(block.fields) ? block.fields.length : 0,
			apiName: blockApiName(block, metadata),
			simple: isSimpleBlock(block),
			active: block.status !== "inactive",
		};
	});
}

export function moveBlock(
	config: TemplateConfig,
	from: number,
	to: number,
): TemplateConfig {
	const blocks = [...(config.blocks ?? [])];
	const [moved] = blocks.splice(from, 1);
	blocks.splice(to, 0, moved);
	return { ...config, blocks };
}

export function removeBlock(
	config: TemplateConfig,
	index: number,
): TemplateConfig {
	return {
		...config,
		blocks: (config.blocks ?? []).filter((_, position) => position !== index),
	};
}

export function summaryLabelsForBlock(
	config: TemplateConfig,
	blockId: string,
): string[] {
	if (!Array.isArray(config.summary)) return [];
	return config.summary
		.filter((item) => item?.block_id === blockId)
		.map((item) => item.label ?? item.id ?? "");
}

export function updateBlock(
	config: TemplateConfig,
	index: number,
	changes: Partial<TemplateBlock>,
): TemplateConfig {
	return {
		...config,
		blocks: (config.blocks ?? []).map((block, position) =>
			position === index ? { ...block, ...changes } : block,
		),
	};
}

export function listSections(
	config: TemplateConfig,
	exceptIndex?: number,
): string[] {
	const sections = (config.blocks ?? [])
		.filter((_, position) => position !== exceptIndex)
		.map((block) => block.section)
		.filter((section): section is string => !!section);
	return Array.from(new Set(sections));
}

export type TemplateField = {
	key: string;
	label?: string;
	format?: string;
	translate?: unknown;
	[key: string]: unknown;
};

export type FieldRow = {
	key: string;
	label: string;
	format: string;
	included: boolean;
	hasTranslation: boolean;
};

export function blockFields(block: TemplateBlock): TemplateField[] {
	return Array.isArray(block.fields) ? (block.fields as TemplateField[]) : [];
}

export function apiFieldKeys(
	block: TemplateBlock,
	metadata: MetadataApiProps[],
): string[] {
	const primary = block.source?.primary;
	const structure = metadata.find((api) => api.id === primary?.api_id)
		?.structure?.[primary?.response_block ?? ""];
	return structure && typeof structure === "object"
		? Object.keys(structure)
		: [];
}

export function blockFieldRows(
	block: TemplateBlock,
	metadata: MetadataApiProps[],
): FieldRow[] {
	const fields = blockFields(block);
	const includedKeys = new Set(fields.map((field) => field.key));
	const included = fields.map((field) => ({
		key: field.key,
		label: field.label ?? "",
		format: field.format ?? "",
		included: true,
		hasTranslation: !!field.translate,
	}));
	const available = apiFieldKeys(block, metadata)
		.filter((key) => !includedKeys.has(key))
		.map((key) => ({
			key,
			label: "",
			format: "",
			included: false,
			hasTranslation: false,
		}));
	return [...included, ...available];
}

export function toggleField(
	block: TemplateBlock,
	key: string,
	included: boolean,
): TemplateField[] {
	const fields = blockFields(block);
	if (!included) return fields.filter((field) => field.key !== key);
	if (fields.some((field) => field.key === key)) return fields;
	return [...fields, { key, label: key, format: "text" }];
}

export function updateField(
	block: TemplateBlock,
	key: string,
	changes: Partial<TemplateField>,
): TemplateField[] {
	return blockFields(block).map((field) =>
		field.key === key ? { ...field, ...changes } : field,
	);
}

export function moveField(
	block: TemplateBlock,
	from: number,
	to: number,
): TemplateField[] {
	const fields = [...blockFields(block)];
	const [moved] = fields.splice(from, 1);
	fields.splice(to, 0, moved);
	return fields;
}

export function slugify(text: string): string {
	return text
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");
}

// ordem das chaves = ordem de cima pra baixo no card renderizado
export function buildBlockSkeleton(
	apiId: string,
	responseBlock: string,
	fields: Record<string, unknown>,
): TemplateBlock {
	return {
		id: slugify(responseBlock),
		title: responseBlock,
		status: "active",
		section: "",
		render: "key_value_grid",
		source: {
			type: "api",
			primary: { api_id: apiId, response_block: responseBlock },
		},
		fields: Object.keys(fields).map((key) => ({
			key,
			label: key,
			format: "text",
		})),
	};
}

function uniqueBlockId(config: TemplateConfig, base: string): string {
	const used = new Set((config.blocks ?? []).map((block) => block.id));
	if (!used.has(base)) return base;
	let suffix = 2;
	while (used.has(`${base}_${suffix}`)) suffix += 1;
	return `${base}_${suffix}`;
}

export function addBlock(
	config: TemplateConfig,
	apiId: string,
	responseBlock: string,
): TemplateConfig {
	const block = buildBlockSkeleton(apiId, responseBlock, {});
	return {
		...config,
		blocks: [
			...(config.blocks ?? []),
			{ ...block, id: uniqueBlockId(config, block.id ?? "") },
		],
	};
}
